import React, { memo, useMemo, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import Animated, { runOnJS, withTiming, useDerivedValue, useAnimatedStyle, useAnimatedProps, } from 'react-native-reanimated';
import { rem, Spacer } from 'design-system';
import Text, { styles as textStyles } from 'design-system/Text';
import { AnimatedTextInput } from 'design-system/AnimatedComponents';
import useStyles from 'hooks/useStyles';
import createStyleSheets from 'utils/createStyleSheets';
import { isWeb } from '../../../../constants';
const gap = rem(6);
// The tooltip's free side. 'auto' on every platform: Reanimated 4 keeps a
// value it set earlier when the next style leaves it undefined, so the box
// stayed pinned on both sides after the cursor crossed the middle.
const defaultLeftAndRight = 'auto';
const AssetsVsDebtInfo = ({ width, selectedInfoObj }) => {
    const styles = useStyles(styleSet);
    const animatedStyles = useAnimatedStyle(() => {
        if (!width || !selectedInfoObj.value) {
            return {
                opacity: withTiming(0),
            };
        }
        const { x } = selectedInfoObj.value;
        if (x > 0.49 * width) {
            return {
                opacity: withTiming(1),
                right: width - x + gap,
                left: defaultLeftAndRight,
            };
        }
        return {
            left: x + gap,
            opacity: withTiming(1),
            right: defaultLeftAndRight,
        };
    }, [selectedInfoObj, width]);
    const [webState, setWebAssetState] = useState(undefined);
    useDerivedValue(() => {
        if (isWeb && selectedInfoObj.value) {
            runOnJS(setWebAssetState)(selectedInfoObj.value);
        }
    }, [selectedInfoObj]);
    const assetsProps = useAnimatedProps(() => {
        if (!isWeb && selectedInfoObj.value?.formattedAssetAmount) {
            return {
                text: selectedInfoObj.value.formattedAssetAmount,
            };
        }
        return {};
    }, [selectedInfoObj]);
    const debtProps = useAnimatedProps(() => {
        if (!isWeb && selectedInfoObj.value?.formattedDebtAmount) {
            return {
                text: selectedInfoObj.value.formattedDebtAmount,
            };
        }
        return {};
    }, [selectedInfoObj]);
    const PriceContent = useMemo(() => {
        if (isWeb) {
            return (<>
          <Text NumbersThin-12>{webState?.formattedAssetAmount || ' '}</Text>
          <Text NumbersThin-12 Secondary>
            {webState?.formattedDebtAmount || ' '}
          </Text>
        </>);
        }
        return (<>
        <AnimatedTextInput editable={false} defaultValue={''} numberOfLines={1} allowFontScaling={false} style={styles.assetStyles} animatedProps={assetsProps}/>
        <AnimatedTextInput editable={false} defaultValue={''} numberOfLines={1} allowFontScaling={false} animatedProps={debtProps} style={styles.debtStyles}/>
      </>);
    }, [
        assetsProps,
        debtProps,
        styles.assetStyles,
        styles.debtStyles,
        webState?.formattedAssetAmount,
        webState?.formattedDebtAmount,
    ]);
    return (<Animated.View style={styles.container} pointerEvents="none">
      <Animated.View style={[styles.flex, animatedStyles]}>
        <View style={styles.infoContainer}>
          <View>
            <Text TextThin-12 Secondary>
              Assets:
            </Text>
            <Spacer h={6}/>
            <Text TextThin-12 Secondary>
              Debt:
            </Text>
          </View>
          <Spacer w={8}/>
          <View style={styles.priceContainer}>{PriceContent}</View>
        </View>
      </Animated.View>
    </Animated.View>);
};
export default memo(AssetsVsDebtInfo);
const styleSet = createStyleSheets((colors) => ({
    container: {
        ...StyleSheet.absoluteFillObject,
        flexDirection: 'row',
    },
    infoContainer: {
        padding: rem(12),
        borderRadius: rem(8),
        flexDirection: 'row',
        backgroundColor: colors.background.light,
        ...colors.shadows.cardLarge,
    },
    priceContainer: {
        alignItems: 'flex-end',
        justifyContent: 'space-between',
    },
    flex: {
        top: 0,
        bottom: 0,
        position: 'absolute',
        flexDirection: 'row',
        alignItems: 'center',
    },
    assetStyles: {
        ...textStyles.NumbersThin_12,
        margin: 0,
        padding: 0,
        height: rem(18),
        color: colors.text.primary,
    },
    debtStyles: {
        ...textStyles.NumbersThin_12,
        margin: 0,
        padding: 0,
        height: rem(18),
        color: colors.text.primary,
    },
}));
